/**
 * Canada Abroad — Phone Input (intl-tel-input wrapper)
 *
 * Exposes window.CA_Phone so any template across the theme can initialise
 * an intl-tel-input widget with one call and never repeat configuration.
 *
 * Usage (from any inline script or JS file):
 *
 *   var iti = CA_Phone.init({
 *     phoneInput : document.getElementById('my_phone'),
 *     isoInput   : document.getElementById('my_iso'),   // hidden <input name="...country_code">
 *     formEl     : document.getElementById('my_form'),  // optional — syncs ISO + validates on submit
 *     country    : 'za',                                // optional — override default country (pk)
 *     dropdownContainer : document.body,                // optional — append country list to body (fixes overflow:hidden ancestors)
 *   });
 *
 * Returns the iti instance so callers can use iti.getNumber() etc.
 */
(function (global) {
	'use strict';

	var ITI_VERSION = '25.10.1';
	var ITI_UTILS_MODULE_URL = 'https://cdn.jsdelivr.net/npm/intl-tel-input@' + ITI_VERSION + '/build/js/utils.js';

	var PREFERRED = ['ca', 'us', 'gb', 'za', 'pk', 'au', 'nz', 'in'];

	/**
	 * Characters allowed in the phone number input.
	 * Allows digits, spaces, hyphens, parentheses only.
	 * A leading + is stripped automatically by nationalMode.
	 */
	var ALLOWED_CHARS = /[^\d\s\-().]/g;

	/**
	 * Initialise intl-tel-input on a phone input element.
	 *
	 * @param {object} opts
	 * @param {HTMLInputElement}  opts.phoneInput  Required. The <input type="tel"> to enhance.
	 * @param {HTMLInputElement}  [opts.isoInput]  Hidden input that holds the ISO-2 code.
	 * @param {HTMLFormElement}   [opts.formEl]    Form — syncs ISO and validates before submit.
	 * @param {string}            [opts.country]   Initial country ISO-2 (default: 'auto' — detected by IP, falls back to 'ca').
	 * @param {string[]}          [opts.preferred] Override preferred countries list.
	 * @param {Node|null}         [opts.dropdownContainer] Append country dropdown here (e.g. document.body) when a parent uses overflow:hidden.
	 * @param {boolean}           [opts.strictMode=true]   Cap typing to max valid length for country (intl strictMode; requires utils).
	 * @returns {object|null}  The iti instance, or null if intlTelInput not yet loaded.
	 */
	function init(opts) {
		var intlTelInputFn = global.intlTelInput;
		if (typeof intlTelInputFn !== 'function') {
			if (typeof console !== 'undefined') {
				console.warn('CA_Phone.init: intlTelInput is not loaded yet.');
			}
			return null;
		}

		var phoneInput = opts.phoneInput;
		var isoInput   = opts.isoInput   || null;
		var formEl     = opts.formEl     || null;
		var country    = opts.country    || 'auto';
		var preferred  = opts.preferred  || PREFERRED;
		var dropdownContainer = opts.dropdownContainer;
		if (dropdownContainer && dropdownContainer.nodeType !== 1) {
			dropdownContainer = null;
		}
		var useStrictMode = opts.strictMode !== false;

		if (!phoneInput) return null;

		/* ── Insert validation error element after the input ────── */
		var errorMsg = document.createElement('span');
		errorMsg.className = 'ca-iti-error-msg';
		errorMsg.setAttribute('aria-live', 'polite');
		errorMsg.setAttribute('role', 'alert');
		phoneInput.parentNode.insertBefore(errorMsg, phoneInput.nextSibling);

		/* ── Initialise iti (v25+: loadUtils + countryOrder) ─────── */
		var itiOpts = {
			initialCountry:   country,
			geoIpLookup: country === 'auto' ? function (success) {
				fetch('https://ipinfo.io/json')
					.then(function (r) { return r.json(); })
					.then(function (d) { success((d && d.country) ? d.country : 'ca'); })
					.catch(function () { success('ca'); });
			} : undefined,
			countryOrder:     preferred,
			separateDialCode: true,
			nationalMode:     true,
			autoPlaceholder:  'aggressive',
			loadUtils: function () {
				return import(/* webpackIgnore: true */ ITI_UTILS_MODULE_URL);
			},
		};
		if (useStrictMode) {
			itiOpts.strictMode = true;
		}
		if (dropdownContainer) {
			itiOpts.dropdownContainer = dropdownContainer;
		}
		var iti = intlTelInputFn(phoneInput, itiOpts);

		/* ── Helpers ────────────────────────────────────────────── */

		function syncIso() {
			if (isoInput) {
				var cd = iti.getSelectedCountryData();
				var iso2 = cd && cd.iso2 ? cd.iso2 : 'ca';
				isoInput.value = String(iso2).toUpperCase();
			}
		}

		function clearError() {
			phoneInput.classList.remove('ca-iti-error');
			errorMsg.textContent = '';
			errorMsg.classList.remove('is-visible');
		}

		function showError(msg) {
			phoneInput.classList.add('ca-iti-error');
			errorMsg.textContent = msg;
			errorMsg.classList.add('is-visible');
		}

		/**
		 * Validate once intl-tel-input utils are loaded (v25+ requires this before isValidNumber).
		 * Returns true if empty (optional field) or if the number is valid.
		 */
		function validateWhenUtilsReady() {
			var val = phoneInput.value.trim();
			if (val === '') {
				clearError();
				return true;
			}
			try {
				if (typeof iti.isValidNumber === 'function' && !iti.isValidNumber()) {
					showError('Please enter a valid phone number for the selected country.');
					return false;
				}
			} catch (err) {
				showError('Please wait a moment, then try again.');
				return false;
			}
			clearError();
			return true;
		}

		function runValidationAfterUtils() {
			var val = phoneInput.value.trim();
			if (val === '') {
				clearError();
				return;
			}
			var p = iti.promise;
			if (p && typeof p.then === 'function') {
				p.then(function () {
					validateWhenUtilsReady();
				}).catch(function () {});
			} else {
				validateWhenUtilsReady();
			}
		}

		/* ── Input filter: strip characters not allowed in phone numbers */
		phoneInput.addEventListener('input', function () {
			var cursor = this.selectionStart;
			var original = this.value;
			var filtered = original.replace(ALLOWED_CHARS, '');
			if (filtered !== original) {
				this.value = filtered;
				/* Restore cursor position after stripping */
				var diff = original.length - filtered.length;
				this.setSelectionRange(cursor - diff, cursor - diff);
			}
			/* Clear error as user types */
			if (this.value.trim() !== '') {
				clearError();
			}
		});

		/* ── Validate on blur (after utils load in v25+) ───────── */
		phoneInput.addEventListener('blur', runValidationAfterUtils);

		/* ── Re-validate / clear error when country changes ────── */
		phoneInput.addEventListener('countrychange', function () {
			syncIso();
			if (phoneInput.value.trim() !== '') {
				runValidationAfterUtils();
			}
		});

		syncIso(); // set ISO on first load

		/* ── Sync ISO + validate before form submits (v25: await iti.promise) ─ */
		if (formEl) {
			var bypassAttr = 'data-ca-phone-submit-bypass';
			formEl.addEventListener('submit', function (e) {
				/* Second pass after requestSubmit(): must not stop other listeners (e.g. AJAX submit). */
				if (formEl.getAttribute(bypassAttr) === '1') {
					formEl.removeAttribute(bypassAttr);
					return;
				}
				syncIso();
				var val = phoneInput.value.trim();

				function block() {
					e.preventDefault();
					e.stopImmediatePropagation();
					phoneInput.focus();
				}

				if (val === '') {
					if (!validateWhenUtilsReady()) {
						block();
					}
					return;
				}

				var p = iti.promise;
				if (p && typeof p.then === 'function') {
					e.preventDefault();
					e.stopImmediatePropagation();
					p.then(function () {
						if (!validateWhenUtilsReady()) {
							phoneInput.focus();
							return;
						}
						formEl.setAttribute(bypassAttr, '1');
						try {
							if (typeof formEl.requestSubmit === 'function') {
								formEl.requestSubmit();
							} else {
								var subBtn = formEl.querySelector('button[type="submit"],input[type="submit"]');
								if (subBtn && typeof subBtn.click === 'function') {
									subBtn.click();
								} else {
									formEl.removeAttribute(bypassAttr);
								}
							}
						} catch (err) {
							formEl.removeAttribute(bypassAttr);
						}
					}).catch(function () {
						phoneInput.focus();
					});
					return;
				}

				if (!validateWhenUtilsReady()) {
					block();
				}
			}, true); // capture phase: runs before the form's own handler
		}

		return iti;
	}

	global.CA_Phone = { init: init };

}(window));
